import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ResumeAdmin = () => {
  const API_BASE_URL =
    process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_API_URL_PRODUCTION
      : process.env.REACT_APP_API_URL;

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchResume = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/resume`);
        setForm(res.data);
      } catch (err) {
        toast.error('Failed to fetch resume');
      }
    };
    fetchResume();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNestedChange = (section, index, key, value) => {
    const updated = [...form[section]];
    updated[index][key] = value;
    setForm({ ...form, [section]: updated });
  };

  const removeItem = (section, index) => {
    const updated = [...form[section]];
    updated.splice(index, 1);
    setForm({ ...form, [section]: updated });
  };

  const addSkill = () => setForm({ ...form, skills: [...form.skills, ''] });
  const addExperience = () =>
    setForm({
      ...form,
      experience: [...form.experience, { company: '', position: '', period: '', description: '' }],
    });
  const addEducation = () =>
    setForm({
      ...form,
      education: [...form.education, { institution: '', degree: '', year: '' }],
    });
  const addProject = () =>
    setForm({
      ...form,
      projects: [...form.projects, { name: '', link: '', description: '' }],
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { _id, __v, ...safePayload } = form;
      await axios.put(`${API_BASE_URL}/api/resume`, safePayload);
      toast.success('Resume updated!');
    } catch (err) {
      console.error('Error updating resume:', err);
      toast.error('Failed to update resume');
    } finally {
      setLoading(false);
    }
  };

  if (!form) return <div>Loading...</div>;

  return (
    <div className="admin-resume">
      <h2>Edit Resume</h2>
      <form onSubmit={handleSubmit}>
        <label>Name</label>
        <input name="name" value={form.name} onChange={handleChange} />

        <label>Title</label>
        <input name="title" value={form.title} onChange={handleChange} />

        <label>Summary</label>
        <textarea name="summary" value={form.summary} onChange={handleChange} />

        <label>Avatar URL</label>
        <input name="avatarUrl" value={form.avatarUrl} onChange={handleChange} />

        <label>Logo URL</label>
        <input name="logoUrl" value={form.logoUrl} onChange={handleChange} />

        {/* ✅ New Field: Resume PDF Link */}
        <label>Resume PDF Link</label>
        <input name="resumeLink" value={form.resumeLink} onChange={handleChange} />

        {/* Skills */}
        <h4>Skills</h4>
        <div className="form-section">
          {form.skills.map((skill, i) => (
            <div className="nested-group" key={i}>
              <label>Skill {i + 1}</label>
              <input
                value={skill}
                onChange={(e) => {
                  const updated = [...form.skills];
                  updated[i] = e.target.value;
                  setForm({ ...form, skills: updated });
                }}
              />
              <button type="button" className="remove-btn" onClick={() => removeItem('skills', i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="add-btn" onClick={addSkill}>
            + Add Skill
          </button>
        </div>

        {/* Experience */}
        <h4>Experience</h4>
        <div className="form-section">
          {form.experience.map((exp, i) => (
            <div className="nested-group" key={i}>
              <label>Position</label>
              <input
                value={exp.position}
                onChange={(e) => handleNestedChange('experience', i, 'position', e.target.value)}
              />
              <label>Company</label>
              <input
                value={exp.company}
                onChange={(e) => handleNestedChange('experience', i, 'company', e.target.value)}
              />
              <label>Period</label>
              <input
                value={exp.period}
                onChange={(e) => handleNestedChange('experience', i, 'period', e.target.value)}
              />
              <label>Description</label>
              <textarea
                value={exp.description}
                onChange={(e) => handleNestedChange('experience', i, 'description', e.target.value)}
              />
              <button type="button" className="remove-btn" onClick={() => removeItem('experience', i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="add-btn" onClick={addExperience}>
            + Add Experience
          </button>
        </div>

        {/* Education */}
        <h4>Education</h4>
        <div className="form-section">
          {form.education.map((edu, i) => (
            <div className="nested-group" key={i}>
              <label>Institution</label>
              <input
                value={edu.institution}
                onChange={(e) => handleNestedChange('education', i, 'institution', e.target.value)}
              />
              <label>Degree</label>
              <input
                value={edu.degree}
                onChange={(e) => handleNestedChange('education', i, 'degree', e.target.value)}
              />
              <label>Year</label>
              <input
                value={edu.year}
                onChange={(e) => handleNestedChange('education', i, 'year', e.target.value)}
              />
              <button type="button" className="remove-btn" onClick={() => removeItem('education', i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="add-btn" onClick={addEducation}>
            + Add Education
          </button>
        </div>

        {/* Projects */}
        <h4>Projects</h4>
        <div className="form-section">
          {form.projects.map((proj, i) => (
            <div className="nested-group" key={i}>
              <label>Project Name</label>
              <input
                value={proj.name}
                onChange={(e) => handleNestedChange('projects', i, 'name', e.target.value)}
              />
              <label>Project Link</label>
              <input
                value={proj.link}
                onChange={(e) => handleNestedChange('projects', i, 'link', e.target.value)}
              />
              <label>Project Description</label>
              <textarea
                value={proj.description}
                onChange={(e) => handleNestedChange('projects', i, 'description', e.target.value)}
              />
              <button type="button" className="remove-btn" onClick={() => removeItem('projects', i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="add-btn" onClick={addProject}>
            + Add Project
          </button>
        </div>

        {/* Contact */}
        <h4>Contact</h4>
        <div className="form-section">
          <div className="nested-group">
            <label>Email</label>
            <input
              name="contact.email"
              value={form.contact?.email || ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  contact: { ...form.contact, email: e.target.value },
                })
              }
            />
            <label>Phone</label>
            <input
              name="contact.phone"
              value={form.contact?.phone || ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  contact: { ...form.contact, phone: e.target.value },
                })
              }
            />
            <label>Location</label>
            <input
              name="contact.location"
              value={form.contact?.location || ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  contact: { ...form.contact, location: e.target.value },
                })
              }
            />
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

export default ResumeAdmin;
